'use client';

/**
 * FeaturedBanner — paid placement banner for marketplace.
 *
 * Feature (v135 — Founder directive 2026-11-04): "banner de productos
 * destacados que los markets puedan pagar por aparecer allí con sus
 * creditos igual que en mercadolibre".
 *
 * Flow:
 *   1. Fetches GET /api/market/products/featured-paid?limit=5
 *   2. If empty, renders nothing (banner is hidden — no paid placements)
 *   3. If 1+, renders a horizontal scrolling banner with product cards
 *   4. Each card links to the product detail page
 *   5. A "Destacado" badge appears on each card (transparency — users know
 *      it's a paid placement, like MercadoLibre's "Patrocinado" badge)
 *
 * Anti-§6: pure UI — fetches data from the public endpoint, no business logic.
 * Anti-P-384: smoke test via agent-browser required (touched frontend).
 */

import { useEffect, useState } from 'react';
import Link from 'next/link';
import type { Route } from 'next';
import { Crown, Sparkles } from 'lucide-react';
import { Card } from '@/components/ui/card';
import type { PublicProductResponse } from '@profileia/shared';

interface FeaturedBannerProps {
  limit?: number;
}

export function FeaturedBanner({ limit = 5 }: FeaturedBannerProps) {
  const [featured, setFeatured] = useState<PublicProductResponse[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/market/products/featured-paid?limit=${limit}`,
        );
        if (!res.ok) {
          if (!cancelled) setFeatured([]);
          return;
        }
        const json = await res.json();
        if (!cancelled) {
          setFeatured(json.data ?? []);
        }
      } catch {
        if (!cancelled) setFeatured([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [limit]);

  // Don't render anything while loading (avoid layout shift)
  if (loading) {
    return null;
  }

  // Don't render if no paid placements (banner is hidden)
  if (!featured || featured.length === 0) {
    return null;
  }

  return (
    <section
      aria-label="Productos destacados"
      className="mb-6"
    >
      <div className="flex items-center gap-2 mb-3">
        <Crown className="size-5 text-amber-500" aria-hidden="true" />
        <h2 className="text-lg font-semibold">Productos destacados</h2>
        <span className="text-xs text-muted-foreground ml-1">
          (patrocinado)
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin">
        {featured.map((product) => (
          <Link
            key={product.productId}
            href={`/market/${product.seller.storeSlug}/${product.slug}` as Route}
            className="snap-start shrink-0 w-64 group"
          >
            <Card className="overflow-hidden h-full transition-shadow hover:shadow-md">
              <div className="relative aspect-square bg-muted">
                {product.images[0] ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={product.images[0]}
                    alt={product.title}
                    className="size-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex items-center justify-center size-full">
                    <Sparkles className="size-8 text-muted-foreground" aria-hidden="true" />
                  </div>
                )}
                <span className="absolute top-2 left-2 rounded-full bg-amber-500/90 px-2 py-0.5 text-xs font-medium text-white shadow-sm">
                  Destacado
                </span>
              </div>
              <div className="p-3">
                <p className="text-sm font-medium line-clamp-1 group-hover:text-primary transition-colors">
                  {product.title}
                </p>
                <p className="text-sm text-muted-foreground">
                  {product.priceCredits} créditos
                </p>
                <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                  {product.seller.storeName}
                </p>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
}
